import { useCallback, useState } from 'react';

import styled from '@emotion/styled';
import {
  Button,
  Colors,
  Input,
  LinkLine,
  typographyStyle,
  PlusLine,
  Flex,
  Text,
  MailLine,
  IconSuggestionLine,
  IconXLine,
} from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast, CloseX } from '@revenuemarket/ui';
import { mixpanelTrack } from '@revenuemarket/utils';
import { ModalProps, Typography } from 'antd';
import dayjs from 'dayjs';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { InviteLinkItem } from '~apis/member';
import { RoleType } from '~apis/role/type';
import RoleSelectDropdown from '~components/Setting/RoleSelectDropdown';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useCompanyMember } from '~hooks/queries/companies/query';
import { useRoleList } from '~hooks/queries/role/query';
import {
  useCancelInvitation,
  useCreateInviteLink,
  useInviteMemberBulk,
  useUpdateInviteRole,
} from '~hooks/queries/users/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { useUserId } from '~hooks/useUserId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

import CommonModal from './CommonModal';

interface Props extends ModalProps {}

const InviteMemberModal = ({ ...props }: Props) => {
  const userId = useUserId();
  const { companyId } = useCompanyId();
  const [currentTab, setCurrentTab] = useState<'email' | 'link'>('email');
  const { mutateAsync: inviteMemberBulk } = useInviteMemberBulk(companyId);
  const { mutateAsync: createInviteLink } = useCreateInviteLink(companyId);
  const { mutateAsync: cancelInviteLink } = useCancelInvitation(companyId);
  const { mutateAsync: updateInviteRole } = useUpdateInviteRole(companyId);
  const { data: roleList } = useRoleList(companyId);
  const roleListData = roleList?.Data?.data?.items || [];
  const { data: companyUserDetail } = useCompanyMember({ companyId, memberId: userId });
  const companyUserDetailData = companyUserDetail?.Data?.data;
  const [emailInput, setEmailInput] = useState<string>('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [linkRoleId, setLinkRoleId] = useState<number | undefined>(undefined);
  const [invitedList, setInvitedList] = useState<{ email: string; roleId: number }[]>([]);
  const [invitedLinkResult, setInvitedLinkResult] = useState<InviteLinkItem | null>(null);
  // 관리자 Role ID, fallback은 사용자
  const defaultRoleId =
    roleListData.find((role) => role.type === RoleType.SYSTEM_MANAGER)?.id ??
    roleListData.find((role) => role.type === RoleType.SYSTEM_DEFAULT)?.id;
  const currentLinkRoleId = linkRoleId ?? defaultRoleId ?? 0;
  const isOwner = companyUserDetailData?.roles?.some((role) => {
    return roleListData.find((r) => r.id === role.roleId)?.type === RoleType.SYSTEM_OWNER;
  });

  const disabledAddEmail = emailInput.length === 0 || !!emailError;

  const handleFinish = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      mixpanelTrack(MIXPANEL_EVENT.USER.CLICK_CONFIRM_INVITE_MEMBER);
      const response = await inviteMemberBulk(invitedList);
      if (response.Status === ResponseStatus.SUCCESS) {
        Toast.success('구성원 초대가 완료되었습니다.');
      } else {
        Toast.error('일부 구성원 초대에 실패했습니다.');
      }
      props.onCancel?.(e);
    },
    [props, invitedList, inviteMemberBulk],
  );

  const handleEmailChange = useCallback((email: string) => {
    setEmailInput(email);
    try {
      if (email.length === 0) {
        setEmailError(null);
        return;
      }
      COMMON_YUP.email.validateSync(email);
      setEmailError(null);
    } catch (err: any) {
      setEmailError(err.message);
    }
  }, []);

  const handleAddEmail = useCallback(
    (email: string) => {
      if (disabledAddEmail) {
        return;
      }
      if (invitedList.some((item) => item.email === email)) {
        Toast.error('이미 추가된 이메일입니다.');
        return;
      }
      setInvitedList((prev) => [...prev, { email, roleId: defaultRoleId ?? 0 }]);
      setEmailInput('');
      setEmailError(null);
    },
    [defaultRoleId, disabledAddEmail, invitedList],
  );

  const handleChangeRole = useCallback((email: string, roleId: number) => {
    setInvitedList((prev) =>
      prev.map((item) => (item.email === email ? { ...item, roleId } : item)),
    );
  }, []);

  const handleRemoveEmail = useCallback((email: string) => {
    setInvitedList((prev) => prev.filter((item) => item.email !== email));
  }, []);

  const handleCopy = useCallback((link: string) => {
    navigator.clipboard.writeText(link);
    Toast.success('링크가 복사되었습니다.');
  }, []);

  const handleCreateInviteLink = useCallback(async () => {
    const response = await createInviteLink(currentLinkRoleId);
    if (response.Status === ResponseStatus.SUCCESS) {
      handleCopy(response.Data?.data?.link ?? '');
      setInvitedLinkResult(response.Data?.data ?? null);
    }
    setLinkRoleId(undefined);
  }, [createInviteLink, handleCopy, currentLinkRoleId]);

  const handleCancelInviteLink = useCallback(
    async (linkId: number) => {
      const response = await cancelInviteLink(linkId);
      if (response.Status === ResponseStatus.SUCCESS) {
        Toast.success('링크가 삭제되었습니다.');
        setInvitedLinkResult(null);
      } else {
        Toast.error('링크 삭제에 실패했습니다.');
      }
    },
    [cancelInviteLink],
  );

  const handleUpdateInviteRole = useCallback(
    async (linkId: number, roleId: number) => {
      const response = await updateInviteRole({ invitationId: linkId, roleId });
      if (response.Status === ResponseStatus.SUCCESS) {
        Toast.success('역할이 변경되었습니다.');
        setInvitedLinkResult((prev) => (prev ? { ...prev, roleId } : null));
      }
    },
    [updateInviteRole],
  );

  return (
    <CommonModal
      width={483}
      footer={null}
      closable={false}
      title={
        <Flex align="center" justify="space-between">
          <Typography.Text
            style={{
              ...typographyStyle['16_600'],
              color: Colors.gray800,
            }}
          >
            구성원 초대
          </Typography.Text>
          <Button
            onClick={props.onCancel}
            border
            buttonType="weak"
            icon={<CloseX width={18} height={18} />}
          />
        </Flex>
      }
      {...props}
    >
      <Flex
        direction="column"
        gap={16}
        style={{
          paddingTop: 16,
        }}
      >
        <SegmentedWrapper>
          {[
            {
              value: 'email',
              label: '이메일로 초대',
              icon: <MailLine width={18} height={18} />,
            },
            {
              value: 'link',
              label: '링크로 초대',
              icon: <LinkLine width={18} height={18} />,
            },
          ].map((item) => (
            <Segmented
              key={item.value}
              align="center"
              active={currentTab === item.value}
              onClick={() => setCurrentTab(item.value as 'email' | 'link')}
            >
              {item.icon}
              <Text textStyle="14_400">{item.label}</Text>
            </Segmented>
          ))}
        </SegmentedWrapper>

        {currentTab === 'email' && (
          <Flex direction="column" gap={16}>
            <Flex
              align="flex-start"
              gap={7}
              style={{ width: '100%' }}
              className={css`
                & > div {
                  flex: 1;
                }
              `}
            >
              <Input
                value={emailInput}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="이메일을 입력해주세요."
                inputStatus={emailError ? 'error' : undefined}
                additionalText={emailError || undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail(emailInput);
                    return;
                  }
                }}
              />
              <Button
                buttonType="weak"
                icon={<IconSuggestionLine width={18} height={18} />}
                onClick={() => handleAddEmail(emailInput)}
                disabled={disabledAddEmail}
              >
                엔터로 추가
              </Button>
            </Flex>

            {invitedList.length > 0 && (
              <OverlayScrollbarsComponent
                style={{
                  height: '100%',
                  maxHeight: 112,
                  overflowY: 'scroll',
                  paddingRight: invitedList.length > 3 ? 12 : 0,
                }}
              >
                <Flex direction="column" gap={8}>
                  {invitedList.map((item) => (
                    <Flex key={item.email} gap={4} align="center">
                      <Text textStyle="14_400" style={{ color: Colors.gray800, flex: 1 }}>
                        {item.email}
                      </Text>
                      <RoleSelectDropdown
                        selectedRoleId={item.roleId}
                        isOwner={isOwner ?? false}
                        onRoleChange={(roleId) => handleChangeRole(item.email, Number(roleId))}
                      />
                      <Button
                        icon={<IconXLine width={18} height={18} fill={Colors.gray700} />}
                        onClick={() => handleRemoveEmail(item.email)}
                      />
                    </Flex>
                  ))}
                </Flex>
              </OverlayScrollbarsComponent>
            )}

            <Button
              buttonType="primary"
              buttonSize="large"
              style={{ width: 'fit-content', alignSelf: 'flex-end' }}
              disabled={invitedList.length === 0}
              onClick={handleFinish}
            >
              초대메일 보내기
            </Button>
          </Flex>
        )}

        {currentTab === 'link' && (
          <Flex direction="column" gap={16}>
            {invitedLinkResult ? (
              <Flex direction="column" gap={8}>
                <Flex align="center" gap={8}>
                  <Text textStyle="14_400" style={{ color: Colors.gray600 }}>
                    링크 만료일시: {dayjs(invitedLinkResult.expiredAt).format('YYYY-MM-DD HH:mm')}
                  </Text>
                  <Button buttonType="primary" onClick={() => handleCopy(invitedLinkResult.link)}>
                    링크 복사
                  </Button>
                  <Button onClick={() => handleCancelInviteLink(invitedLinkResult.id)}>
                    링크 삭제
                  </Button>
                </Flex>
                <Flex
                  align="center"
                  gap={8}
                  style={{ padding: 8, borderRadius: 10, backgroundColor: Colors.gray100 }}
                >
                  <Text
                    textStyle="14_400"
                    style={{ color: Colors.gray600, userSelect: 'none' }}
                    ellipsis
                  >
                    {invitedLinkResult.link}
                  </Text>
                  <RoleSelectDropdown
                    selectedRoleId={invitedLinkResult.roleId ?? 0}
                    isOwner={isOwner ?? false}
                    onRoleChange={(roleId) =>
                      handleUpdateInviteRole(invitedLinkResult.id, Number(roleId))
                    }
                  />
                </Flex>
              </Flex>
            ) : (
              <>
                <Text textStyle="14_400" style={{ color: Colors.gray600 }}>
                  초대 링크를 공유하면 상대방이 워크스페이스에 참여할 수 있어요
                </Text>
                <Flex align="center" gap={8}>
                  <RoleSelectDropdown
                    selectedRoleId={currentLinkRoleId}
                    isOwner={isOwner ?? false}
                    onRoleChange={(roleId) => setLinkRoleId(Number(roleId))}
                  />
                  <Button
                    buttonType="weak"
                    style={{
                      width: 'fit-content',
                    }}
                    icon={<PlusLine />}
                    onClick={() => {
                      handleCreateInviteLink();
                    }}
                  >
                    초대 링크 만들기
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        )}
      </Flex>
    </CommonModal>
  );
};

export default InviteMemberModal;

const SegmentedWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 2px;
  border-radius: 10px;
  background-color: #0000000a;
  width: fit-content;
`;

const Segmented = styled(Flex)<{ active: boolean }>`
  height: 100%;
  gap: 4px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${({ active }) => (active ? Colors.white : 'transparent')};
  padding: 5px 12px;
`;
